import Dexie, { type EntityTable } from "dexie";
import {
  activeReviews,
  dayKey,
  defaults,
  deviceZone,
  eventSchema,
  id,
  validExample,
  wordKey,
  wordSchema,
  exampleSchema,
  settingsSchema,
  type Deck,
  type WordSense,
  type ExampleSentence,
  type Flashcard,
  type ReviewState,
  type ReviewEvent,
  type StudySession,
  type UserSettings,
  type Snapshot,
  type Mode,
} from "./domain";
import { newState, schedule } from "./scheduler";

export class SentenceDB extends Dexie {
  words!: EntityTable<WordSense, "id">;
  examples!: EntityTable<ExampleSentence, "id">;
  cards!: EntityTable<Flashcard, "id">;
  states!: EntityTable<ReviewState, "id">;
  events!: EntityTable<ReviewEvent, "id">;
  sessions!: EntityTable<StudySession, "id">;
  decks!: EntityTable<Deck, "id">;
  settings!: EntityTable<UserSettings, "id">;
  constructor(name = "sentence") {
    super(name);
    this.version(1).stores({
      words: "id,lemma,*deckIds,*tags",
      examples: "id,wordId",
      cards: "id,wordId,direction",
      states: "id,cardId,memory.dueAt",
      events: "id,wordId,cardId,at,sessionId,localDay",
      sessions: "id,startedAt",
      decks: "id",
      settings: "id",
    });
    this.version(2)
      .stores({ events: "id,wordId,cardId,at,sessionId,localDay,kind" })
      .upgrade(async (tx) => {
        await tx
          .table("settings")
          .toCollection()
          .modify((s) => Object.assign(s, { ...defaults, ...s }));
        await tx
          .table("events")
          .toCollection()
          .modify((e) => {
            e.kind ??= "review";
          });
      });
  }
}
export const db = new SentenceDB();
export const tables = (d: SentenceDB) => [
  d.words,
  d.examples,
  d.cards,
  d.states,
  d.events,
  d.sessions,
  d.decks,
  d.settings,
];
export async function initialize(d = db) {
  await d.transaction("rw", d.settings, d.decks, async () => {
    if (!(await d.settings.get("settings"))) await d.settings.add(defaults);
    if (!(await d.decks.count()))
      await d.decks.bulkPut([
        {
          id: "personal",
          name: "Kendi eklediklerim",
          description: "Fielse ve günlük hayatından kelimeler.",
        },
        {
          id: "a1",
          name: "A1 Temel İngilizce",
          description: "İsteğe bağlı başlangıç örnekleri.",
        },
        {
          id: "a2",
          name: "A2 Günlük İngilizce",
          description: "Gündelik durumlar.",
        },
        {
          id: "phrases",
          name: "Common Phrases",
          description: "Birlikte öğrenilen ifadeler.",
        },
        {
          id: "restaurant",
          name: "Restaurant & Hospitality",
          description: "Yemek ve hizmet.",
        },
      ]);
  });
}
export async function snapshot(d = db): Promise<Snapshot> {
  return d.transaction("r", tables(d), async () => {
    const [words, examples, cards, states, events, sessions, decks, settings] =
      await Promise.all([
        d.words.toArray(),
        d.examples.toArray(),
        d.cards.toArray(),
        d.states.toArray(),
        d.events.toArray(),
        d.sessions.toArray(),
        d.decks.toArray(),
        d.settings.get("settings"),
      ]);
    return {
      words,
      examples,
      cards,
      states,
      events,
      sessions,
      decks,
      settings: settings ?? defaults,
    };
  });
}
export async function saveWord(word: WordSense, examples: ExampleSentence[], d = db) {
  word = wordSchema.parse(word);
  examples = examples.map((e) => exampleSchema.parse(e));
  if (
    !examples.length ||
    examples.some((e) => e.wordId !== word.id || !validExample(e))
  )
    throw new Error("Her kelimeye geçerli hedef aralığı olan en az bir cümle ekle.");
  const cards: Flashcard[] = [
    "receptive",
    "productive",
    ...(word.collocations.length ? ["collocation"] : []),
  ].map((direction) => ({
    id: word.id + ":" + direction,
    wordId: word.id,
    direction: direction as Flashcard["direction"],
    createdAt: word.createdAt,
  }));
  const states = await Promise.all(cards.map((c) => newState(c.id)));
  await d.transaction("rw", d.words, d.examples, d.cards, d.states, async () => {
    const duplicate = (await d.words.toArray()).find(
      (w) => !w.deleted && w.id !== word.id && wordKey(w) === wordKey(word),
    );
    if (duplicate)
      throw new Error(
        "Bu kelimenin aynı anlamı zaten kütüphanende. Mevcut kaydı düzenleyebilirsin.",
      );
    await d.words.put({ ...word, exampleIds: examples.map((e) => e.id) });
    // Editors create new example IDs so sentences referenced by past events remain available.
    await d.examples.bulkPut(examples);
    for (let i = 0; i < cards.length; i++)
      if (!(await d.cards.get(cards[i].id))) {
        await d.cards.add(cards[i]);
        await d.states.add(states[i]);
      }
  });
}
export interface ReviewInput {
  cardId: string;
  exampleId: string;
  sessionId: string;
  mode: Mode;
  answer: string;
  correct: boolean | null;
  prediction: ReviewEvent["prediction"];
  rating: number;
  hint: boolean;
  translation: boolean;
  answerMs: number;
  activeMs: number;
  expectedVersion: number;
  prompt?: string;
  expected?: string;
}
export async function recordReview(input: ReviewInput, d = db, now = new Date()) {
  const before = await d.states.get(input.cardId);
  const card = await d.cards.get(input.cardId);
  const settings = await d.settings.get("settings");
  if (!before || !card || !settings)
    throw new Error("Kart bulunamadı. Çalışmayı yeniden aç.");
  // An incorrect verified response is Again. Hinted or pre-reveal forgotten answers cannot become unassisted successes.
  const effective =
    input.correct === false || input.prediction === "forgot"
      ? 1
      : input.hint
        ? Math.min(input.rating, 2)
        : input.rating;
  const { after, revlog } = await schedule(before, effective, now, settings.retention);
  const word = await d.words.get(card.wordId);
  if (!word || word.deleted)
    throw new Error("Bu kelime silinmiş. Çalışmayı yeniden aç.");
  const event = eventSchema.parse({
    id: id(),
    wordId: card.wordId,
    ...input,
    at: now.toISOString(),
    timezone: deviceZone(),
    localDay: dayKey(now),
    direction: card.direction,
    assessment: input.correct === null ? "self" : "automatic",
    effectiveRating: effective,
    previousDue: before.memory.dueAt,
    nextDue: after.memory.dueAt,
    difficult: word.difficult,
    wordAtReview: word.lemma,
    meaningAtReview: word.meaning,
    before,
    after,
    revlog,
    kind: "review",
  });
  await d.transaction("rw", d.states, d.events, d.sessions, async () => {
    const latest = await d.states.get(card.id);
    if (
      !latest ||
      latest.version !== input.expectedVersion ||
      latest.version !== before.version
    )
      throw new Error("Kart başka bir sekmede değişti. Oturumu yeniden aç.");
    if (!(await d.sessions.get(input.sessionId)))
      throw new Error("Oturum bulunamadı. Çalışmayı yeniden başlat.");
    await d.events.add(event);
    await d.states.put(after);
  });
  return event;
}
export async function undoReview(eventId: string, d = db) {
  return d.transaction("rw", d.events, d.states, async () => {
    const e = await d.events.get(eventId);
    if (!e || e.kind !== "review") throw new Error("Geri alınacak çalışma bulunamadı.");
    const all = await d.events.where("cardId").equals(e.cardId).toArray();
    if (!activeReviews(all).some((x) => x.id === e.id))
      throw new Error("Bu çalışma zaten geri alınmış.");
    const current = await d.states.get(e.cardId);
    if (!current || current.version !== e.after.version)
      throw new Error(
        "Bu kart yeniden çalışıldığı için önceki değerlendirme geri alınamaz.",
      );
    const restored = { ...e.before, version: current.version + 1 };
    await d.events.add({
      ...e,
      id: id(),
      kind: "undo",
      undoOf: e.id,
      at: new Date().toISOString(),
      localDay: dayKey(new Date()),
      timezone: deviceZone(),
      before: current,
      after: restored,
      previousDue: current.memory.dueAt,
      nextDue: restored.memory.dueAt,
    });
    await d.states.put(restored);
  });
}
export async function deleteWord(wordId: string, d = db) {
  await d.words.update(wordId, { deleted: true, active: false });
}
export async function updateSettings(patch: Partial<UserSettings>, d = db) {
  await d.transaction("rw", d.settings, async () => {
    const s = (await d.settings.get("settings")) ?? defaults;
    await d.settings.put(settingsSchema.parse({ ...s, ...patch }));
  });
}
